import React, { useCallback, useId } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { BrowseCardAnalyticsContext, CardProps } from '../../common/types';
import { Card } from '../../common/ui/Card/Card';
import { CardContent, type CardContentProps } from '../../common/ui/Card/CardContent';
import { CardCover, type CardCoverProps } from '../../common/ui/Card/CardCover';
import { CardFooter } from '../../common/ui/Card/CardFooter';
import { StudioBrowseKitIntlProvider } from '../../i18n/StudioBrowseKitIntlProvider';
import { AgentMetadata, type AgentMetadataProps } from './components/AgentMetadata';
import type { AgentCardAgent } from './types';

export type AgentCardCoverProps = CardCoverProps;
export type AgentCardContentProps = CardContentProps;
export type AgentBadgeProps = {
	agent: AgentCardAgent;
	testId?: string;
};
export type AgentCardFooterProps = {
	agent: AgentCardAgent;
	testId?: string;
};

type AgentCardBaseProps = Omit<CardProps, 'ariaLabel' | 'children' | 'onClick'> & {
	analyticsContext?: BrowseCardAnalyticsContext;
	onClick?: (agentId: string, analyticsContext?: BrowseCardAnalyticsContext) => void;
	slots?: {
		cover?: React.ComponentType<AgentCardCoverProps> | null;
		content?: React.ComponentType<AgentCardContentProps> | null;
		metadata?: React.ComponentType<AgentMetadataProps>;
		badge?: React.ComponentType<AgentBadgeProps>;
		footer?: React.ComponentType<AgentCardFooterProps> | null;
	};
	testId?: string;
};

export type AgentCardProps = AgentCardBaseProps & {
	agent: AgentCardAgent;
};

const styles = cssMap({
	favourite: {
		// @ts-expect-error -- strict cssMap API does not model hover media queries
		'@media (hover: hover)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& [data-card-favourite="false"]': {
				opacity: 0,
				pointerEvents: 'none',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover [data-card-favourite="false"], &:focus-within [data-card-favourite="false"]': {
				opacity: 1,
				pointerEvents: 'auto',
			},
		},
	},
});

const VerifiedBadge = (): React.JSX.Element => (
	<StatusVerifiedIcon label="Verified" size="small" color={token('color.icon.information')} />
);

export const AgentCard = ({
	agent,
	analyticsContext,
	onClick,
	isDisabled,
	slots = {},
	testId,
	xcss,
	...cardProps
}: AgentCardProps): React.JSX.Element => {
	const isUplift = expVal('jira_browse_agent_modal_update', 'isEnabled', false);
	const descriptionId = useId();
	const {
		cover: CoverComponent = CardCover,
		content: ContentComponent = CardContent,
		metadata: MetadataComponent = isUplift ? undefined : AgentMetadata,
		badge: BadgeComponent,
		footer: FooterComponent,
	} = slots;

	const handleClick = useCallback(() => {
		if (analyticsContext) {
			onClick?.(agent.id, { ...analyticsContext, interactionElement: 'card' });
		} else {
			onClick?.(agent.id);
		}
	}, [analyticsContext, onClick, agent.id]);

	const badge =
		BadgeComponent && CoverComponent ? (
			<Box data-card-badge testId={testId ? `${testId}--badge` : undefined}>
				<BadgeComponent agent={agent} testId={testId ? `${testId}--badge-content` : undefined} />
			</Box>
		) : null;

	return (
		<StudioBrowseKitIntlProvider>
			<Card
				{...cardProps}
				xcss={cx(isUplift && styles.favourite, xcss)}
				isDisabled={isDisabled}
				hasCutout={Boolean(CoverComponent)}
				ariaLabel={agent.name}
				aria-describedby={agent.description ? descriptionId : undefined}
				onClick={onClick ? handleClick : undefined}
				testId={testId}
			>
				{CoverComponent ? (
					<CoverComponent
						name={agent.name}
						iconUrl={agent.iconUrl}
						coverColor={agent.coverColor}
						isDisabled={isDisabled}
						testId={testId ? `${testId}--cover` : undefined}
						badge={badge}
					/>
				) : null}
				{ContentComponent ? (
					<ContentComponent
						name={agent.name}
						description={agent.description}
						descriptionId={descriptionId}
						isDisabled={isDisabled}
						subtitle={isUplift ? agent.creatorName : undefined}
						badge={agent.isVerified ? <VerifiedBadge /> : undefined}
						testId={testId ? `${testId}--content` : undefined}
					/>
				) : null}
				{MetadataComponent ? (
					<MetadataComponent agent={agent} testId={testId ? `${testId}--metadata` : undefined} />
				) : null}
				{FooterComponent ? (
					<CardFooter testId={testId ? `${testId}--footer` : undefined}>
						<FooterComponent
							agent={agent}
							testId={testId ? `${testId}--footer-content` : undefined}
						/>
					</CardFooter>
				) : null}
			</Card>
		</StudioBrowseKitIntlProvider>
	);
};
